"use client";

import { Loader2, Star } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  CvExperienceField,
  CvFileField,
  CvPurposeField,
} from "@/components/cv-form/cv-upload-form-mobile-fields";
import { CvTalentPoolChoiceField } from "@/components/cv-form/cv-talent-pool-choice-field";
import { CvReviewLanguageField } from "@/components/cv-form/cv-review-language-field";
import { CvTargetingSection } from "@/components/cv-form/cv-upload-form-mobile-targeting";
import {
  useCvUploadFormMobile,
  type CvUploadMobilePayload,
  type Purpose,
} from "@/components/cv-form/use-cv-upload-form-mobile";

interface CVUploadFormMobileProps {
  onUpload: (payload: CvUploadMobilePayload) => Promise<void>;
  isUploading: boolean;
  className?: string;
  isSignedIn?: boolean;
}

export function CVUploadFormMobile({
  onUpload,
  isUploading,
  className,
  isSignedIn = false,
}: CVUploadFormMobileProps) {
  const form = useCvUploadFormMobile({ onUpload, isUploading, isSignedIn });
  const { dictionary } = form;
  const { fields, options, actions } = dictionary.cvUploadForm;
  const heroStats = dictionary.landing.heroSection.stats;

  return (
    <Card
      className={cn(
        "w-full max-w-[343px] gap-0 overflow-hidden rounded-[8px] border-0 bg-transparent py-0 shadow-none",
        className,
      )}
    >
      <div className="flex w-full items-center justify-center rounded-t-[8px] bg-surface-inverse px-14 py-[7px] dark:bg-surface-secondary">
        <div className="flex items-center gap-1 text-[12px] font-medium leading-[18px] text-white dark:text-text-heading">
          <span>{heroStats.count}</span>
          <span aria-hidden="true">&bull;</span>
          <span className="flex items-center gap-1">
            <Star
              className="size-[10px] text-white dark:text-text-heading"
              fill="currentColor"
            />
            <span>{heroStats.rating}</span>
          </span>
        </div>
      </div>
      <CardContent className="bg-surface-primary px-4 pb-5 pt-4">
        <form
          onSubmit={form.handleSubmit}
          className="space-y-4"
          aria-describedby={form.error ? "cv-upload-form-error" : undefined}
        >
          <div className="space-y-4">
            <CvFileField
              fields={fields}
              file={form.file}
              attemptedSubmit={form.attemptedSubmit}
              requiredMessage={form.requiredMessages.file}
              inputRef={form.fileInputRef}
              onChange={form.handleFileChange}
            />

            <CvPurposeField
              fields={fields}
              options={options.reviewPurposes}
              purpose={form.purpose}
              attemptedSubmit={form.attemptedSubmit}
              requiredMessage={form.requiredMessages.purpose}
              firstOptionRef={form.purposeRef}
              onSelect={(value) => form.setPurpose(value as Purpose)}
            />

            {form.purpose ? (
              <CvTargetingSection
                fields={fields}
                isOpen={form.showTargetingFields}
                onToggle={form.toggleTargetingFields}
                showProgramCampus={form.purpose === "scholarship"}
                targetJobTitle={form.targetJobTitle}
                onTargetJobTitleChange={form.setTargetJobTitle}
                programCampus={form.programCampus}
                onProgramCampusChange={form.setProgramCampus}
                jobDescription={form.jobDescription}
                onJobDescriptionChange={form.setJobDescription}
              />
            ) : null}

            <CvExperienceField
              fields={fields}
              options={options.experienceLevels}
              experienceLevel={form.experienceLevel}
              attemptedSubmit={form.attemptedSubmit}
              requiredMessage={form.requiredMessages.experience}
              triggerRef={form.experienceRef}
              onChange={form.setExperienceLevel}
            />

            <CvReviewLanguageField
              idPrefix="mobile"
              size="mobile"
              value={form.reviewLanguage}
              onChange={form.setReviewLanguage}
            />
          </div>

          <CvTalentPoolChoiceField
            idPrefix="mobile"
            size="mobile"
            value={form.talentPoolChoice}
            onChange={form.setTalentPoolChoice}
            showError={form.attemptedSubmit && form.talentPoolChoice === null}
          />

          {form.error ? (
            <Alert id="cv-upload-form-error" variant="destructive">
              <AlertDescription>{form.error}</AlertDescription>
            </Alert>
          ) : null}

          {!form.isSignedIn ? (
            <p className="text-[13px] leading-5 text-text-muted">
              {dictionary.cvUploadForm.googleAccountHint}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={form.isSubmitDisabled}
            className={cn(
              "h-11 w-full rounded-[8px] text-[14px] font-semibold leading-5 shadow-button",
              form.isSubmitDisabled
                ? "bg-text-disabled hover:bg-text-disabled text-foreground"
                : "bg-brand-primary hover:bg-brand-primary/90 text-white",
            )}
          >
            {isUploading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                {form.isSignedIn
                  ? actions.uploading
                  : dictionary.authForms.login.connecting}
              </span>
            ) : form.isSignedIn ? (
              actions.upload
            ) : (
              actions.signUpAndReview
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
